import { useState } from "react";
import { PageHeader, Card, CardHeader, AtomBadge, MetricTile, StatusPill, EmptyState } from "../components/primitives";
import { Search, Upload, Settings2, ChevronDown, ChevronRight, Calendar, FileDown, Repeat, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const LISTS = [
  { id: "ofac_sdn", name: "OFAC SDN", count: 12420, src: "treasury.gov/ofac", upd: "06:00 today", group: "international" },
  { id: "un_1267", name: "UN Consolidated (1267)", count: 980, src: "scsanctions.un.org", upd: "06:00 today", group: "international" },
  { id: "un_1718", name: "UN 1718 (DPRK)", count: 380, src: "scsanctions.un.org", upd: "06:00 today", group: "international" },
  { id: "eu_cfsp", name: "EU CFSP Consolidated", count: 2104, src: "eeas.europa.eu", upd: "06:00 today", group: "international" },
  { id: "uk_ofsi", name: "UK HMT OFSI", count: 1842, src: "gov.uk/ofsi", upd: "06:00 today", group: "international" },
  { id: "au_dfat", name: "Australia DFAT", count: 1208, src: "dfat.gov.au", upd: "06:00 today", group: "international" },
  { id: "nscdf", name: "NSCDF / ONSA", count: 84, src: "onsa.gov.ng", upd: "Yesterday", group: "nigerian" },
  { id: "nfiu", name: "NFIU Designations", count: 142, src: "nfiu.gov.ng", upd: "Yesterday", group: "nigerian" },
  { id: "efcc", name: "EFCC Watchlist", count: 308, src: "efcc.gov.ng liaison", upd: "20 May 2026", group: "nigerian" },
  { id: "wc_pep", name: "World-Check PEP", count: 2_400_000, src: "Refinitiv stream", upd: "Continuous", group: "commercial" },
  { id: "dj_am", name: "Dow Jones Adverse Media", count: 850_000, src: "Dow Jones feed", upd: "Continuous", group: "commercial" },
  { id: "internal_wl", name: "Internal Watchlist", count: 1284, src: "Internal", upd: "Today 09:14", group: "internal" },
  { id: "internal_cwp", name: "Closed With Prejudice", count: 412, src: "Internal", upd: "Today 09:14", group: "internal" },
  { id: "internal_probation", name: "Probation Register", count: 320, src: "Internal", upd: "Today 09:14", group: "internal" },
  { id: "cbn_domestic", name: "CBN Domestic Register", count: 156, src: "CBN feed", upd: "15 May 2026", group: "nigerian" },
];

// Seeded match catalogue
const HITS = [
  { name: "Adel Mahmoud Al-Qasimi", list: "OFAC SDN", score: 94, exact: 0, jw: 96, ph: 91, tl: 97, tp: 89, band: "REVIEW REQUIRED", aliases: ["Adel Al-Qasemi", "A. M. Al-Qassimi", "عادل القاسمي"], dob: "1971-03-14", country: "Yemen", roles: "Listed individual", listed: "2019-08-22", refresh: "Today 06:00", evidence: "Identifier match · DOB confirmed", searchTerms: ["adel", "qasimi", "qasemi", "qassimi", "qasim"] },
  { name: "Hassan Mohammed Yusuf", list: "UN Consolidated", score: 91, exact: 0, jw: 93, ph: 95, tl: 88, tp: 86, band: "REVIEW REQUIRED", aliases: ["H. M. Yusuf", "Hassan Mohamed Yusef"], dob: "1965-02-09", country: "Somalia", roles: "Al-Qaeda affiliation", listed: "2012-04-19", refresh: "Today 06:00", evidence: "Phonetic + transliteration", searchTerms: ["hassan", "yusuf", "yusef", "yusoof"] },
  { name: "Boris Petrovich Vasilyev", list: "OFAC SSI · Russia sectoral", score: 87, exact: 0, jw: 88, ph: 84, tl: 91, tp: 82, band: "REVIEW REQUIRED", aliases: ["Б. П. Васильев", "Boris P. Vasiliev"], dob: "1968-11-22", country: "Russia", roles: "Sectoral sanctioned", listed: "2022-04-08", refresh: "Today 06:00", evidence: "Cyrillic transliteration", searchTerms: ["boris", "vasilyev", "vasiliev", "petrovich"] },
  { name: "Mohammad Reza Khorasani", list: "UN Consolidated · Iran", score: 89, exact: 0, jw: 91, ph: 93, tl: 95, tp: 79, band: "REVIEW REQUIRED", aliases: ["محمد رضا خراسانی", "M. R. Khorasanee"], dob: "1972-07-04", country: "Iran", roles: "Nuclear programme designation", listed: "2018-05-08", refresh: "Today 06:00", evidence: "Arabic transliteration", searchTerms: ["mohammad", "khorasani", "reza", "khorasanee"] },
  { name: "Park Jin-Hyok", list: "OFAC SDN · DPRK", score: 93, exact: 0, jw: 89, ph: 98, tl: 91, tp: 87, band: "REVIEW REQUIRED", aliases: ["박진혁", "Pak Jin Hyok"], dob: "1984-08-15", country: "DPRK", roles: "Lazarus Group cyber", listed: "2018-09-06", refresh: "Today 06:00", evidence: "Phonetic match strong", searchTerms: ["park", "jin", "hyok", "pak"] },
  { name: "Sen. Ibrahim Tijani Adamu", list: "Domestic PEP", score: 98, exact: 100, jw: 96, ph: 96, tl: 90, tp: 95, band: "HIGH CONFIDENCE", aliases: ["I. T. Adamu", "Tijani I. Adamu"], dob: "1968-04-22", country: "Nigeria", roles: "Senator · 2023-present", listed: "2023-06-12", refresh: "Continuous", evidence: "BVN match", searchTerms: ["ibrahim", "adamu", "tijani"] },
  { name: "Aisha Bello", list: "Domestic PEP relative", score: 88, exact: 0, jw: 92, ph: 89, tl: 84, tp: 82, band: "REVIEW REQUIRED", aliases: ["Aisha Bello-Ahmed"], dob: "1975-11-08", country: "Nigeria", roles: "Spouse of Permanent Secretary", listed: "2022-03-04", refresh: "Continuous", evidence: "Relationship: spouse", searchTerms: ["aisha", "bello"] },
  { name: "Sanusi Hassan", list: "Internal Watchlist", score: 84, exact: 0, jw: 86, ph: 89, tl: 79, tp: 81, band: "LOGGED", aliases: ["S. Hassan"], dob: "1980-09-12", country: "Nigeria", roles: "Velocity anomaly · 14d", listed: "2026-05-09", refresh: "Today 09:14", evidence: "Behavioural", searchTerms: ["sanusi", "hassan"] },
  { name: "Mohammed Ibrahim", list: "OFAC SDN (partial)", score: 76, exact: 0, jw: 78, ph: 82, tl: 70, tp: 71, band: "LOGGED", aliases: ["M. Ibrahim"], dob: "1974-02-18 (list) · search DOB unspecified", country: "Sudan (list) · search Nigeria", roles: "Distinguished by DOB+country", listed: "2017-11-30", refresh: "Today 06:00", evidence: "Distinguished — likely false positive", searchTerms: ["mohammed", "ibrahim"] },
];

const bandStyle = (band) => band === "HIGH CONFIDENCE" ? "bg-risk-veryhigh text-white" : band === "REVIEW REQUIRED" ? "bg-amber-500 text-white" : band === "LOGGED" ? "bg-amber-300 text-amber-900" : "bg-ink-muted/20 text-ink-muted";

function matchSearch(q) {
  const ql = q.toLowerCase().trim();
  if (!ql) return [];
  const hits = HITS.filter(h => h.searchTerms.some(t => ql.includes(t) || t.includes(ql))).sort((a, b) => b.score - a.score);
  return hits;
}

export default function Screening() {
  const [tab, setTab] = useState("single");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [filterMode, setFilterMode] = useState("All integrated lists (15)");

  const runSearch = () => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    setTimeout(() => {
      const r = matchSearch(q);
      setResults(r);
      setSearching(false);
      toast.success(`Screened "${q}"`, { description: r.length === 0 ? "No matches above 70%" : `${r.length} matches · logged to audit trail` });
    }, 320);
  };

  return (
    <div data-testid="screening-page">
      <PageHeader
        title="Sanctions and PEP Screening"
        subtitle="Live name screening, bulk batch screening, and integrated list administration."
        sections={["5.3"]}
        atom={<AtomBadge model="Atom Electron" version="v1.1" latency="89ms p95" />}
      />

      {/* Tabs */}
      <div className="border-b border-line bg-bg-surface px-7">
        <div className="flex gap-1">
          {[["single", "Single Name Search", Search], ["bulk", "Bulk Screening", Upload], ["lists", "List Administration", Settings2]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-3 text-[12px] border-b-2 transition-colors inline-flex items-center gap-2 ${tab === k ? "border-amber-600 text-ink font-medium" : "border-transparent text-ink-muted hover:text-ink"}`}>
              <Icon size={13} strokeWidth={1.5} />{l}
            </button>
          ))}
        </div>
      </div>

      <div className="p-7 space-y-5">
        {tab === "single" && (
          <>
            {/* Search workspace */}
            <Card>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 border-2 border-line rounded-lg px-3 py-2.5 focus-within:border-amber-600 transition-colors">
                  <Search size={16} className="text-ink-subtle" />
                  <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Enter customer name, BVN, NIN, or counterparty name to screen against all integrated lists." className="flex-1 bg-transparent text-[14px] outline-none" />
                  <button data-testid="search-btn" onClick={runSearch} className="px-4 py-1.5 text-[12px] bg-navy text-white rounded-md hover:bg-amber-600 font-medium">Search</button>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className="inline-flex border border-line rounded-md p-0.5 bg-bg-warm/40 text-[11px]">
                    {["Single name", "Bulk upload", "Test against historical hits"].map((m, i) => (
                      <button key={m} onClick={() => i === 1 && setTab("bulk")} className={`px-2.5 py-1 rounded transition-colors ${i === 0 ? "bg-bg-surface text-ink font-medium shadow-sm" : "text-ink-muted hover:text-ink"}`}>{m}</button>
                    ))}
                  </div>
                  <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} className="text-[11px] border border-line rounded-md px-2 py-1 bg-bg-surface">
                    <option>All integrated lists (15)</option><option>OFAC only</option><option>Nigerian regulatory only</option><option>Internal watchlist only</option><option>Commercial PEP only</option>
                  </select>
                  <button onClick={() => setStrategyOpen(!strategyOpen)} className="text-[11px] text-navy inline-flex items-center gap-1 hover:text-amber-600">
                    Match strategy controls {strategyOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                </div>

                {strategyOpen && (
                  <div className="bg-bg-warm/40 border border-line rounded-lg p-4 grid grid-cols-5 gap-3 text-[11px]">
                    {[["Exact match", 100, true, true], ["Jaro-Winkler", 85, true, false], ["Phonetic (Soundex+Metaphone)", 90, true, false], ["Transliteration (Arabic/Cyrillic)", 85, true, false], ["Token permutation", 95, true, false]].map(([n, th, on, locked]) => (
                      <div key={n} className="bg-bg-surface border border-line rounded-md p-3">
                        <div className="flex items-center justify-between"><span className="font-medium text-ink">{n}</span><input type="checkbox" defaultChecked={on} disabled={locked} className="w-3.5 h-3.5" /></div>
                        <div className="text-ink-subtle mt-1.5">Threshold</div>
                        <div className="flex items-center gap-1.5"><input type="range" min={70} max={100} defaultValue={th} className="flex-1 accent-amber-600" /><span className="tabular text-ink font-medium">{th}%</span></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            {/* Results */}
            {searching ? (
              <Card>
                <div className="p-8 text-center text-[12px] text-ink-muted"><div className="inline-block w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin mr-2" />Screening across 15 lists with 5 strategies…</div>
              </Card>
            ) : q.trim() && results.length === 0 ? (
              <Card>
                <div className="p-8 text-center">
                  <ShieldCheck size={32} className="mx-auto text-risk-low mb-3" strokeWidth={1.4} />
                  <div className="text-[14px] font-medium text-ink">No matches above 70% confidence threshold</div>
                  <div className="text-[12px] text-ink-muted mt-1">Searched across 15 integrated lists. This search has been logged to the customer's screening history.</div>
                  <div className="text-[11px] text-ink-subtle mt-3 tabular">{new Date().toLocaleString("en-GB")} · searched by Ava Holloway · <a className="text-navy underline">view audit entry</a></div>
                </div>
              </Card>
            ) : results.length > 0 && (
              <div className="space-y-3">
                <div className="text-[11px] text-ink-subtle">{results.length} matches sorted by composite score · Powered by Atom Electron v1.1</div>
                {results.map((r, i) => <MatchCard key={i} r={r} />)}
              </div>
            )}
          </>
        )}

        {tab === "bulk" && <BulkUploadTab />}
        {tab === "lists" && <ListAdmin />}
      </div>
    </div>
  );
}

function MatchCard({ r }) {
  const [expanded, setExpanded] = useState(false);
  const strategies = [["Exact", r.exact], ["Jaro-Winkler", r.jw], ["Phonetic", r.ph], ["Transliteration", r.tl], ["Token permutation", r.tp]];
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="display text-[15px] font-semibold text-ink">{r.name}</span>
              <span className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-navy/10 text-navy border border-navy/20">{r.list}</span>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">Listed since {r.listed} · last refresh {r.refresh}</div>
          </div>
          <div className="text-right">
            <div className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${bandStyle(r.band)}`}>{r.band}</div>
            <div className="display text-[28px] font-semibold tabular text-ink mt-1.5 leading-none">{r.score}%</div>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 mt-3">
          {strategies.map(([s, v]) => (
            <div key={s} className="bg-bg-warm/40 border border-line rounded p-2">
              <div className="text-[9px] uppercase tracking-wide text-ink-subtle">{s}</div>
              <div className="flex items-end justify-between mt-1"><span className="tabular text-ink font-medium text-[13px]">{v}%</span></div>
              <div className="h-1 bg-bg-warm rounded-full mt-1"><div className="h-full bg-navy rounded-full" style={{ width: `${v}%` }} /></div>
            </div>
          ))}
        </div>

        <button onClick={() => setExpanded(!expanded)} className="text-[11px] text-navy mt-3 inline-flex items-center gap-1">
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Supporting evidence
        </button>
        {expanded && (
          <div className="grid grid-cols-2 gap-3 mt-2 text-[12px] bg-bg-warm/30 p-3 rounded-lg border border-line">
            <div className="space-y-1.5">
              <div><span className="text-ink-subtle">Aliases:</span> <span className="text-ink">{r.aliases.join(" · ") || "—"}</span></div>
              <div><span className="text-ink-subtle">DOB:</span> <span className="text-ink">{r.dob}</span></div>
              <div><span className="text-ink-subtle">Country:</span> <span className="text-ink">{r.country}</span></div>
            </div>
            <div className="space-y-1.5">
              <div><span className="text-ink-subtle">Role:</span> <span className="text-ink">{r.roles}</span></div>
              <div><span className="text-ink-subtle">Evidence:</span> <span className="text-ink">{r.evidence}</span></div>
              <div><span className="text-ink-subtle">Source:</span> <span className="text-ink">{r.list}</span></div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button data-testid="open-case" onClick={() => toast.success("Case opened from match")} className="px-2.5 py-1 text-[11px] bg-navy text-white rounded-md hover:bg-amber-600">Open full case</button>
          <button onClick={() => toast.success("Marked as cleared", { description: "Reason logged to audit trail" })} className="px-2.5 py-1 text-[11px] border border-line rounded-md text-ink-muted hover:bg-bg-warm">Mark as cleared</button>
          <button onClick={() => toast.success("Escalated to MLRO")} className="px-2.5 py-1 text-[11px] border border-risk-high/40 text-risk-high rounded-md hover:bg-risk-high/8">Escalate to MLRO</button>
        </div>
      </div>
    </Card>
  );
}

function BulkUploadTab() {
  const [stage, setStage] = useState("idle"); // idle | uploaded | processing | done
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [recurring, setRecurring] = useState(false);

  const onUpload = () => {
    setFile({ name: "wema_q3_review_batch.xlsx", names: 87 });
    setStage("uploaded");
  };
  const onRun = () => {
    setStage("processing");
    setProgress(0);
    const id = setInterval(() => setProgress((p) => { if (p >= 100) { clearInterval(id); setStage("done"); return 100; } return p + 12; }), 280);
  };

  const results = stage === "done" ? [
    { row: 1, name: "Adel Mahmoud Al-Qasimi", ref: "WEMA-Q3-001", status: "HIT", score: 94, lists: ["OFAC SDN"] },
    { row: 2, name: "Hassan Mohammed Yusuf", ref: "WEMA-Q3-002", status: "HIT", score: 91, lists: ["UN 1267"] },
    { row: 3, name: "Park Jin-Hyok", ref: "WEMA-Q3-003", status: "HIT", score: 93, lists: ["OFAC SDN · DPRK"] },
    { row: 4, name: "Sen. Ibrahim Tijani Adamu", ref: "WEMA-Q3-004", status: "REVIEW", score: 88, lists: ["Domestic PEP"] },
    { row: 5, name: "Aisha Bello", ref: "WEMA-Q3-005", status: "REVIEW", score: 88, lists: ["PEP relative"] },
    { row: 6, name: "Mohammed Ibrahim", ref: "WEMA-Q3-006", status: "REVIEW", score: 76, lists: ["OFAC SDN partial"] },
    { row: 7, name: "Olumide Adeyemi", ref: "WEMA-Q3-007", status: "CLEAR", score: 0, lists: [] },
    { row: 8, name: "Folake Ogundipe", ref: "WEMA-Q3-008", status: "REVIEW", score: 78, lists: ["Internal Watchlist"] },
    { row: 9, name: "Chinwe Okeke", ref: "WEMA-Q3-009", status: "CLEAR", score: 0, lists: [] },
    { row: 10, name: "Babatunde Adeyemi", ref: "WEMA-Q3-010", status: "REVIEW", score: 82, lists: ["Internal Watchlist"] },
  ] : [];
  const statusStyle = (s) => s === "HIT" ? "bg-risk-veryhigh text-white" : s === "REVIEW" ? "bg-amber-500 text-white" : "bg-risk-low/12 text-risk-low border border-risk-low/30";

  return (
    <>
      <div className="text-[12px] text-ink-muted">Upload up to <span className="text-ink font-medium">1,000,000 names</span> per batch. Use the template provided to ensure correct field mapping. Results returned within 30 seconds for batches under 100 names; large batches stream asynchronously.</div>

      <Card>
        <div className="p-5">
          <button data-testid="upload-zone" onClick={onUpload} className="w-full border-2 border-dashed border-line hover:border-navy hover:bg-bg-warm/40 rounded-lg py-12 text-center transition-colors">
            <Upload size={28} className="mx-auto text-ink-subtle mb-2" strokeWidth={1.4} />
            <div className="text-[13px] text-ink font-medium">Drop your Excel file here, or click to browse</div>
            <div className="text-[11px] text-ink-muted mt-1">Accepts .xlsx · max 1,000,000 rows</div>
            <button className="text-[11px] text-navy underline mt-2">Download template (XLSX)</button>
          </button>

          <details className="mt-3 text-[11px] text-ink-muted">
            <summary className="cursor-pointer text-ink hover:text-amber-600 text-[12px]">Template structure</summary>
            <div className="mt-2 grid grid-cols-5 gap-2 text-[11px]">
              {[["A", "Full Name", "required"], ["B", "Date of Birth", "optional · DD/MM/YYYY"], ["C", "Nationality", "optional · ISO code"], ["D", "Identifier", "BVN / NIN / passport"], ["E", "Customer reference", "optional"]].map(([c, n, t]) => (
                <div key={c} className="border border-line rounded p-2 bg-bg-warm/30"><div className="text-ink font-medium">Col {c} · {n}</div><div className="text-ink-subtle text-[10px]">{t}</div></div>
              ))}
            </div>
          </details>

          {file && (
            <div className="mt-4 bg-bg-warm/40 border border-line rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="text-[12px] text-ink font-medium">{file.name}</div>
                <div className="text-[11px] text-ink-muted">{file.names} names parsed · all 5 strategies enabled · all 15 lists in scope</div>
              </div>
              {stage === "uploaded" && <button data-testid="run-batch" onClick={onRun} className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-md hover:bg-amber-600 font-medium">Run Batch Screening</button>}
              {stage === "processing" && <div className="text-[11px] text-amber-600 font-medium">Processing… {progress}%</div>}
              {stage === "done" && <span className="inline-flex items-center gap-1 text-[11px] text-risk-low"><ShieldCheck size={12} />Complete</span>}
            </div>
          )}

          {stage === "processing" && (
            <div className="mt-3 bg-bg-warm/30 border border-line rounded-lg p-3 space-y-2 text-[11px]">
              <div className="flex justify-between"><span>File received</span><span className="text-risk-low">✓</span></div>
              <div className="flex justify-between"><span>Records parsed</span><span className="text-ink">{file?.names || 87}</span></div>
              <div className="flex justify-between"><span>Screening across 15 lists × 5 strategies</span><span className="text-amber-600">{progress}%</span></div>
              <div className="h-1.5 bg-bg-warm rounded-full overflow-hidden"><div className="h-full bg-amber-600 transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
        </div>
      </Card>

      {stage === "done" && (
        <>
          <Card>
            <div className="p-4 grid grid-cols-5 gap-3 items-center">
              <div className="col-span-2">
                <div className="text-[11px] uppercase tracking-wide text-ink-subtle">Summary</div>
                <div className="display text-[16px] text-ink font-medium mt-1">87 names screened · 3 hits · 7 review · 12 weak · 65 cleared</div>
              </div>
              <button onClick={() => toast.success("Results.xlsx exported")} className="px-3 py-1.5 text-[12px] border border-line rounded-md text-ink-muted hover:bg-bg-warm inline-flex items-center gap-1.5"><FileDown size={12} />Export results (XLSX)</button>
              <button onClick={() => toast.success("3 cases created", { description: "Routed to Sanctions Review Queue" })} className="px-3 py-1.5 text-[12px] bg-navy text-white rounded-md hover:bg-amber-600">Create cases for all hits</button>
              <button onClick={() => setRecurring(true)} className="px-3 py-1.5 text-[12px] border border-line rounded-md text-ink-muted hover:bg-bg-warm inline-flex items-center gap-1.5"><Repeat size={12} />Schedule recurring sweep</button>
            </div>
          </Card>

          <Card>
            <CardHeader title="Batch results" subtitle="Click any row to view the full match detail" />
            <table className="w-full text-[12px]">
              <thead className="bg-bg-warm/60"><tr className="text-[10px] uppercase tracking-wide text-ink-subtle">
                {["#", "Searched name", "Reference", "Status", "Score", "Lists", ""].map(h => <th key={h} className="text-left px-3 py-2.5 font-medium">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-line">
                {results.map((r) => (
                  <tr key={r.row} className="hover:bg-bg-warm">
                    <td className="px-3 py-2 tabular text-ink-subtle">{r.row}</td>
                    <td className="px-3 py-2 text-ink">{r.name}</td>
                    <td className="px-3 py-2 text-ink-muted tabular text-[11px]">{r.ref}</td>
                    <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded ${statusStyle(r.status)}`}>{r.status}</span></td>
                    <td className="px-3 py-2 tabular text-ink">{r.score > 0 ? `${r.score}%` : "—"}</td>
                    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{r.lists.map(l => <span key={l} className="inline-block px-1.5 py-0.5 text-[9px] rounded bg-navy/8 text-navy border border-navy/15">{l}</span>)}</div></td>
                    <td className="px-3 py-2"><button className="text-[11px] text-navy hover:text-amber-600">View detail →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {recurring && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setRecurring(false)}>
          <div className="bg-bg-surface rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3"><h3 className="display text-[16px] font-semibold text-ink">Schedule recurring sweep</h3><button onClick={() => setRecurring(false)}><X size={16} /></button></div>
            <div className="space-y-3 text-[12px]">
              <div><div className="text-ink-subtle text-[11px] mb-1">Cadence</div><select className="w-full border border-line rounded-md px-2 py-1.5 bg-bg-surface"><option>Daily</option><option>Weekly</option><option>Monthly</option></select></div>
              <div><div className="text-ink-subtle text-[11px] mb-1">Routing</div><select className="w-full border border-line rounded-md px-2 py-1.5 bg-bg-surface"><option>Sanctions Team</option><option>Compliance Lead</option><option>MLRO</option></select></div>
              <button onClick={() => { setRecurring(false); toast.success("Scheduled · weekly · routed to Sanctions Team"); }} className="w-full bg-navy text-white rounded-md py-2 text-[12px] hover:bg-amber-600">Schedule</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ListAdmin() {
  const groups = [["Nigerian regulatory", "nigerian"], ["International sanctions", "international"], ["Commercial intelligence", "commercial"], ["Internal lists", "internal"]];
  return (
    <div className="grid grid-cols-2 gap-4">
      {groups.map(([title, g]) => (
        <Card key={g}>
          <CardHeader title={title} subtitle={`${LISTS.filter(l => l.group === g).length} lists · maker-checker on configuration changes`} />
          <div>
            {LISTS.filter(l => l.group === g).map((l) => (
              <div key={l.id} className="px-4 py-2.5 border-b border-line last:border-0 flex items-center justify-between">
                <div>
                  <div className="text-[12px] font-medium text-ink">{l.name}</div>
                  <div className="text-[10px] text-ink-subtle">{l.count.toLocaleString()} entries · {l.src}</div>
                </div>
                <div className="text-right">
                  <StatusPill status="Connected" />
                  <div className="text-[10px] text-ink-muted mt-1">Updated {l.upd}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
